import {createAsyncThunk} from "@reduxjs/toolkit";
import {
    getActiveBlock, getAfterMessageBlock, getNextBlock, getNextId, hasAfterMessage, shouldFinishChat, shouldGoNext,
    shouldUpdateActiveGroup
} from "@/helpers/Flow";
import {
    addBlock as addBlockReducer, clearTimerID, finishChat, noData, resetFlow as resetFlowReducer,
    rewindToBlock as rewindToBlockReducer, setActiveGroupId, setData, setDataLoading, setIsSubmittingConversation,
    setTimerID, showLoading
} from "@/store/slices/chatbotFlow";

import {resetSelectedOptions} from "@/store/slices/chatbotInput";
import {apiClient} from "@/helpers/axios";
import {setSecondsPassed, setStartTime} from "@/store/slices/chatbotVisibility";
import BlockTypes from "@/constants/BlockTypes";

export const processFlow = createAsyncThunk(
    'chatbotFlow/processFlow',
    async (input, thunkAPI) => {
        const {
            flow, activeGroupId, conversation, isFinished, loading, asyncMessageTimerID
        } = thunkAPI.getState().chatbotFlow;
        if (conversation.length === 0) { //load the first block if it's not loaded
            thunkAPI.dispatch(addBlock(flow.groups[0].blocks[0]))
        }

        if (isFinished) //don't process flow if the chat has finished
            return


        if (loading || asyncMessageTimerID !== 0) //don't process flow if the chat is in loading state
            return

        let activeBlock = getActiveBlock(conversation) //get Current active block from conversations

        if (!shouldGoNext(activeBlock)) //check if user has entered his/her answer
            return

        if (shouldFinishChat(activeBlock) && !hasAfterMessage(activeBlock)) { //check if next actions are END_CHAT
            thunkAPI.dispatch(finishChat())
            thunkAPI.dispatch(resetSelectedOptions())
            thunkAPI.dispatch(submitConversation())
            return
        }

        let nextBlock = getNextBlock(activeBlock, activeGroupId, flow)

        if (hasAfterMessage(activeBlock)) {
            nextBlock = thunkAPI.dispatch(addBlock(getAfterMessageBlock(activeBlock)));
        }

        if (shouldUpdateActiveGroup(activeBlock)) {
            const newGroupId = getNextId(activeBlock);
            thunkAPI.dispatch(setActiveGroupId(newGroupId)) //it's important to update activeGroupId after finding the next block, not before it.
        }

        if (typeof nextBlock !== 'undefined') { //Check if it's the last block in the group
            if (nextBlock?.blockType === BlockTypes.DATA)
                thunkAPI.dispatch(addBlockReducer(nextBlock))
            else
                thunkAPI.dispatch(addBlock(nextBlock));
        } else {
            thunkAPI.dispatch(finishChat())
            thunkAPI.dispatch(submitConversation())
            thunkAPI.dispatch(resetSelectedOptions())
        }
    }
)

// export const submitConversation = createAsyncThunk(
//     'chatbotFlow/submitConversation',
//     async (_, thunkAPI) => {
//       const { chatbotDetails, chatbotFlow, chatbotVisibility } = thunkAPI.getState();
//       const { UUID, candidateID, sourceID } = chatbotDetails;
//       const { conversation, isFinished, isSubmittingConversation } = chatbotFlow;
  
//       if (isSubmittingConversation) return;
  
//       // Calculate time interval
//       const { startTime, secondsPassed } = chatbotVisibility;
//       const seconds = ((new Date()).getTime() - (new Date(startTime)).getTime()) / 1000;
//       const duration = Math.min(Math.round(secondsPassed + seconds), 32767); 
  
//       // Update state to indicate conversation is being submitted
//       thunkAPI.dispatch(setIsSubmittingConversation(true));
  
//       const payload = {
//         raw_data: conversation,
//         is_completed: isFinished,
//         duration,
//       };
  
//       const queryParams = { id: candidateID, source: sourceID };
  
//       try {
//         const response = await apiClient.post(
//           `/assistants/${UUID}/conversations/`,
//           payload,
//           { params: queryParams }
//         );
  
//         if (response.status === 201) {
//           console.info('Conversation successfully submitted to the server.');
//         } 
//         else {
//           console.error(`Server returned unexpected status: ${response.status}`);
//           offloadToServiceWorker(payload, queryParams, UUID);
//         }
//       } 
//       catch (error) {
//         console.error('Error while submitting conversation to the server:', error);
//         offloadToServiceWorker(payload, queryParams, UUID); // Fallback to Service Worker
//       } 
//       finally {
//         thunkAPI.dispatch(setIsSubmittingConversation(false));
//       }
//     }
//   );
  
export const submitConversation = createAsyncThunk(
    'chatbotFlow/submitConversation',
    async (_, thunkAPI) => {
        const { chatbotDetails, chatbotFlow, chatbotVisibility } = thunkAPI.getState();
        const { UUID, candidateID, sourceID } = chatbotDetails;
        const { conversation, isFinished, isSubmittingConversation } = chatbotFlow;

        if (isSubmittingConversation) return;

        // Calculate time interval
        const { startTime, secondsPassed } = chatbotVisibility;
        const seconds = ((new Date()).getTime() - (new Date(startTime)).getTime()) / 1000;
        const duration = Math.min(Math.round(secondsPassed + seconds), 32767); 

        thunkAPI.dispatch(setIsSubmittingConversation(true));

        const payload = {
        raw_data: conversation,
        is_completed: isFinished,
        duration,
        };

        const queryParams = { id: candidateID, source: sourceID };

        // Submit the conversation to the server via sw
        offloadToServiceWorker(payload, queryParams, UUID); // Capture the response
        thunkAPI.dispatch(setIsSubmittingConversation(false));
        console.info('Conversation successfully submitted to the server via sw.');
        
    }
);

const offloadToServiceWorker = (payload, queryParams, UUID) => {
    navigator.serviceWorker.controller.postMessage({
        type: 'SUBMIT_CONVERSATION',
        data: {
            ...payload,
            params: queryParams,
            UUID,
        }
    });        
};

export const searchCandidates = createAsyncThunk(
    'chatbotFlow/searchCandidates',
    async (action, thunkAPI) => {
        thunkAPI.dispatch(setDataLoading(true))
        const {chatbotDetails, chatbotFlow, chatbotVisibility} = thunkAPI.getState();
        const {UUID} = chatbotDetails;
        const {conversation, isFinished} = chatbotFlow;

        //calculate time interval
        const {startTime, secondsPassed} = chatbotVisibility;
        const seconds = ((new Date()).getTime() - (new Date(startTime)).getTime()) / 1000;
        return apiClient.put(`/assistants/${UUID}/candidate-search/`, {
            raw_data: conversation,
            is_completed: isFinished,
            duration: Math.min(Math.round(secondsPassed + seconds), 32767)
        })
            .then(function (response) {
                // handle success
                if (response.status === 200) {
                    thunkAPI.dispatch(setData(response?.data))
                    if (response?.data?.length === 0)
                        thunkAPI.dispatch(noData())
                } else {
                    thunkAPI.dispatch(setData([]))
                    thunkAPI.dispatch(noData())
                }
                thunkAPI.dispatch(setDataLoading(false))
            })
            .catch(function (error) {
                thunkAPI.dispatch(setDataLoading(false))
                thunkAPI.dispatch(setData([]))
                thunkAPI.dispatch(noData())
                console.error("elay.io chatbot couldn't connect to the server")
                console.error(error)
            })
    }
)

export const searchJobs = createAsyncThunk(
    'chatbotFlow/searchJobs',
    async (action, thunkAPI) => {
        thunkAPI.dispatch(setDataLoading(true))
        const {chatbotDetails, chatbotFlow, chatbotVisibility} = thunkAPI.getState();
        const {UUID} = chatbotDetails;
        const {conversation, isFinished} = chatbotFlow;

        //calculate time interval
        const {startTime, secondsPassed} = chatbotVisibility;
        const seconds = ((new Date()).getTime() - (new Date(startTime)).getTime()) / 1000;
        return apiClient.put(`/assistants/${UUID}/job-search/`, {
            raw_data: conversation,
            is_completed: isFinished,
            duration: Math.min(Math.round(secondsPassed + seconds), 32767)
        })
            .then(function (response) {
                if (response.status === 200) {
                    thunkAPI.dispatch(setData(response?.data))
                    if (response?.data?.length === 0)
                        thunkAPI.dispatch(noData())
                } else {
                    thunkAPI.dispatch(setData([]))
                    thunkAPI.dispatch(noData())
                }
                thunkAPI.dispatch(setDataLoading(false));
            })
            .catch(function (error) {
                thunkAPI.dispatch(setDataLoading(false))
                thunkAPI.dispatch(setData([]))
                thunkAPI.dispatch(noData())
                console.error("elay.io chatbot couldn't connect to the server")
                console.error(error)
            })
    }
)


export const resetFlow = createAsyncThunk(
    'chatbotFlow/resetFlowThunk',
    async (block, thunkAPI) => {
        const state = thunkAPI.getState().chatbotFlow
        clearTimeout(state.asyncMessageTimerID);
        thunkAPI.dispatch(setStartTime(new Date().toISOString()))
        thunkAPI.dispatch(setSecondsPassed(0))
        thunkAPI.dispatch(clearTimerID())
        thunkAPI.dispatch(resetFlowReducer())
        thunkAPI.dispatch(resetSelectedOptions())
    }
)

export const rewindToBlock = createAsyncThunk(
    'chatbotFlow/rewindToBlockThunk',
    async (blockIndex, thunkAPI) => {
        const state = thunkAPI.getState().chatbotFlow
        clearTimeout(state.asyncMessageTimerID);
        thunkAPI.dispatch(clearTimerID())
        thunkAPI.dispatch(rewindToBlockReducer(blockIndex))
    }
)

export const addBlock = createAsyncThunk( //Add a block with loading
    'chatbotFlow/addBlockDelayed',
    async (block, thunkAPI) => {
        const state = thunkAPI.getState().chatbotFlow;
        const numberOfHalfLines = Math.round(block?.message?.length / 15) || 1
        const rawDelay = numberOfHalfLines < 2 ? 800 : numberOfHalfLines * 499
        const delay = rawDelay > 3500 ? 3500 : rawDelay
        if (state.loading === false) {
            clearTimeout(state.asyncMessageTimerID);
            if (block?.options?.default_answer === true) { //if it was default answer, don't show loading and add it to the conversation immediately.
                thunkAPI.dispatch(addBlockReducer(block))
            } else {
                thunkAPI.dispatch(showLoading(true))
                const timerID = setTimeout(() => {
                    thunkAPI.dispatch(addBlockReducer(block))
                    thunkAPI.dispatch(clearTimerID())
                    thunkAPI.dispatch(showLoading(false))
                }, delay)
                thunkAPI.dispatch(setTimerID(timerID))
            }
        }
    }
)